import React, { useEffect, useMemo, useRef, useState } from "react";
import type {
  GlobalSearchResult,
  ModuleSearchResult,
} from "./types";

export interface GlobalSearchDropdownProps {
  open: boolean;
  modules: ModuleSearchResult[];
  anchorRef: React.RefObject<HTMLElement | null>;
  onSelect: (result: GlobalSearchResult) => void;
  onClose: () => void;
}

interface RowAddress {
  moduleIdx: number;
  rowIdx: number;
}

function visibleModules(modules: ModuleSearchResult[]): ModuleSearchResult[] {
  return modules;
}

function firstSelectableRow(modules: ModuleSearchResult[]): RowAddress | null {
  for (let mi = 0; mi < modules.length; mi++) {
    if (modules[mi].state === "ok" && modules[mi].results.length > 0) {
      return { moduleIdx: mi, rowIdx: 0 };
    }
  }
  return null;
}

function step(
  modules: ModuleSearchResult[],
  cur: RowAddress | null,
  dir: 1 | -1,
): RowAddress | null {
  if (cur === null) return firstSelectableRow(modules);
  let { moduleIdx, rowIdx } = cur;
  const total = modules.length;
  const maxSteps = 2 * total + 2;

  for (let i = 0; i < maxSteps; i++) {
    rowIdx += dir;

    if (rowIdx < 0) {
      moduleIdx -= 1;
      if (moduleIdx < 0) return cur;
      const prev = modules[moduleIdx];
      if (prev.state === "ok" && prev.results.length > 0) {
        return { moduleIdx, rowIdx: prev.results.length - 1 };
      }
      rowIdx = 0;
      continue;
    }

    if (moduleIdx >= total) return cur;
    const mod = modules[moduleIdx];
    if (mod.state === "ok" && rowIdx <= mod.results.length - 1) {
      return { moduleIdx, rowIdx };
    }

    moduleIdx += 1;
    if (moduleIdx >= total) return cur;
    rowIdx = -1;
  }

  return cur;
}

export const GlobalSearchDropdown: React.FC<GlobalSearchDropdownProps> = ({
  open,
  modules,
  anchorRef,
  onSelect,
  onClose,
}) => {
  const dropdownRef = useRef<HTMLDivElement>(null);
  const visible = useMemo(() => visibleModules(modules), [modules]);
  const [selected, setSelected] = useState<RowAddress | null>(null);
  const selectedRef = useRef<RowAddress | null>(null);
  selectedRef.current = selected;
  const visibleRef = useRef(visible);
  visibleRef.current = visible;

  useEffect(() => {
    setSelected(firstSelectableRow(visible));
  }, [visible]);

  useEffect(() => {
    if (!open) return;

    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
        return;
      }
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setSelected((cur) => step(visibleRef.current, cur, 1));
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setSelected((cur) => step(visibleRef.current, cur, -1));
        return;
      }
      if (e.key === "Enter") {
        const addr = selectedRef.current ?? firstSelectableRow(visibleRef.current);
        if (!addr) return;
        const mod = visibleRef.current[addr.moduleIdx];
        if (mod && mod.state === "ok") {
          e.preventDefault();
          onSelect(mod.results[addr.rowIdx]);
        }
        return;
      }
    }

    function onMouseDown(e: MouseEvent) {
      const target = e.target as Node;
      const inDropdown = dropdownRef.current?.contains(target) ?? false;
      const inAnchor = anchorRef.current?.contains(target) ?? false;
      if (!inDropdown && !inAnchor) onClose();
    }

    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onMouseDown);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onMouseDown);
    };
  }, [open, anchorRef, onClose, onSelect]);

  if (!open || visible.length === 0) return null;

  return (
    <div
      ref={dropdownRef}
      role="listbox"
      aria-label="Search results"
      className="absolute left-0 right-0 top-full mt-2 max-h-[480px] overflow-y-auto rounded-2xl border border-border bg-background shadow-xl z-50"
    >
      {visible.map((mod, mi) => (
        <section key={mod.moduleId} className="py-2">
          <header className="px-4 py-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            {mod.label}
          </header>

          {mod.state === "loading" &&
            [0, 1, 2].map((i) => (
              <div
                key={i}
                data-testid="global-search-skeleton-row"
                className="mx-2 my-1 h-10 animate-pulse rounded-lg bg-muted"
              />
            ))}

          {mod.state === "error" && (
            <div className="mx-2 my-1 flex items-center justify-between rounded-lg bg-red-50 dark:bg-red-950/30 px-3 py-2 text-sm text-red-700 dark:text-red-300">
              <span>{mod.error?.message ?? "Search failed"}</span>
              <button
                type="button"
                className="rounded-md px-2 py-1 text-xs font-semibold underline"
                onClick={() => mod.refetch()}
              >
                Retry
              </button>
            </div>
          )}

          {mod.state === "ok" && mod.results.length === 0 && (
            <div className="mx-2 my-1 rounded-lg px-3 py-2 text-sm text-muted-foreground">
              No results
            </div>
          )}

          {mod.state === "ok" &&
            mod.results.map((r, ri) => {
              const isSelected =
                selected?.moduleIdx === mi && selected?.rowIdx === ri;
              return (
                <button
                  key={r.id}
                  type="button"
                  role="option"
                  aria-selected={isSelected}
                  onMouseEnter={() => setSelected({ moduleIdx: mi, rowIdx: ri })}
                  onClick={() => onSelect(r)}
                  className={`flex w-full flex-col gap-0.5 px-4 py-2 text-left transition-colors ${
                    isSelected ? "bg-muted" : "hover:bg-muted/60"
                  }`}
                >
                  <span className="truncate text-sm font-medium text-foreground">
                    {r.title}
                  </span>
                  {r.subtitle && (
                    <span className="truncate text-xs text-muted-foreground">
                      {r.subtitle}
                    </span>
                  )}
                </button>
              );
            })}
        </section>
      ))}
    </div>
  );
};
